<template>
  <v-container>
    <ViewHeader :title="viewTitle">
      <div class="d-flex justify-end">
        <ui-button color="primary" outlined class="min-w" @click="handleCancel">
          {{ $t("message.facilityCreate.cancelCreating") }}
        </ui-button>
      </div>
    </ViewHeader>
    <v-col cols="12">
      <v-card class="position-relative">
        <v-row class="px-6">
          <v-col
            cols="6"
            class="facility-create__column facility-create__column--main"
            v-if="loaded && accounts.length"
          >
            <h3 class="facility-create__heading">
              {{ $t("message.facilityCreate.baseForm.heading") }}
            </h3>
            <div class="section">
              <div class="section-title mb-6">
                {{ $t("message.facilityCreate.baseForm.headingSecondary") }}
              </div>

              <template v-for="field in mapFields">
                <v-autocomplete
                  v-if="field.type === 'autocomplete'"
                  :key="field.id"
                  v-model="field.value"
                  v-bind="commonProps(field)"
                  item-text="name"
                  item-value="name"
                  :items="field.items"
                  :loading="field.isLoading"
                  @focus="getCities"
                />
                <v-text-field
                  v-else
                  :key="field.id"
                  v-model="field.value"
                  v-bind="commonProps(field)"
                />
              </template>
            </div>

            <div class="section">
              <div>
                <PrimaryCategoryEdit
                  :loading="false"
                  :is-readonly="false"
                  :primary-category="primaryCategory"
                  info-box-text="message.facilityCreate.primaryCategoryRequired"
                  @save="saveCategory"
                />
              </div>

              <div class="mt-6">
                <AdditionalCategoriesEdit
                  :loading="false"
                  :is-readonly="false"
                  :additional-categories="additionalCategories"
                  info-box-text=""
                  @save="saveCategory"
                  @delete="deleteCategory"
                />
              </div>
            </div>
          </v-col>
          <v-col
            :cols="loaded && accounts.length === 0 ? 12 : 6"
            class="facility-create__column facility-create__column--secondary"
          >
            <div v-if="loaded && accounts.length === 0" class="no-accounts">
              <img
                src="@/static/businessAddresses/noAccounts.svg"
                :alt="$t('message.facilityCreate.accountSelect.noAccounts')"
              />
              <p class="no-accounts__description">
                {{ $t("message.facilityCreate.accountSelect.noAccounts") }}
              </p>
            </div>

            <div v-else-if="loaded && accounts.length > 0">
              <h3 class="facility-create__heading">
                {{ $t("message.facilityCreate.accountSelect.heading") }}
              </h3>
              <div>
                <div class="section-title mb-6">
                  {{
                    $t("message.facilityCreate.accountSelect.headingSecondary")
                  }}
                </div>
              </div>
              <v-autocomplete
                v-model="selectedAccount"
                return-object
                :items="accounts"
                item-text="email"
                item-value="id"
                :hide-details="true"
                :placeholder="$t('message.common.chooseGmbAccount')"
                append-icon="icon-arrow-down"
                full-width
                :menu-props="{
                  offsetY: true
                }"
                @change="handleAccountSelectChange"
                class="facility-create__select"
              >
              </v-autocomplete>

              <v-autocomplete
                :disabled="!selectedAccount"
                v-model="selectedProfile"
                return-object
                :items="selectedAccount ? selectedAccount.gmb_profiles : []"
                item-text="profile_name"
                item-value="id"
                :hide-details="true"
                :placeholder="$t('message.common.chooseProfile')"
                append-icon="icon-arrow-down"
                full-width
                :menu-props="{
                  offsetY: true
                }"
                class="facility-create__select"
                :class="{
                  'facility-create__select--disabled': !selectedAccount
                }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="select-item">
                      {{ item.profile_name }}
                      <span class="select-item__quantity">{{
                        getFacilitiesCountLabel(item.imported_addresses_count)
                      }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </div>

            <ui-button
              v-if="loaded && accounts.length"
              color="primary"
              class="align-self-end min-w--md"
              :disabled="!canProceed"
              @click="handleProceed"
            >
              {{ $t("message.common.further") }}
            </ui-button>
          </v-col>
        </v-row>
        <SectionLoader :status="loading.view" />
      </v-card>
    </v-col>

    <FacilityCreatedDialog
      v-if="dialog.facilityCreated && selectedAccount && selectedProfile"
      :is-open="dialog.facilityCreated"
      :account="selectedAccount.email"
      :profile="selectedProfile.profile_name"
      @createNew="resetForm"
      @close="dialog.facilityCreated = false"
    />
    <FacilityImportedDialog
      :is-open="dialog.facilityImported"
      @close="dialog.facilityImported = false"
    />
    <CreateDuplicateDialog
      v-if="dialog.createDuplicate"
      :is-open="dialog.createDuplicate"
      @create="createFacility"
      @close="dialog.createDuplicate = false"
    />
    <InCenterDialog
      v-if="dialog.inCenter && searchResults && searchResults.length === 1"
      :is-open="dialog.inCenter"
      :facility-data="searchResults && searchResults[0]"
      @close="dialog.inCenter = false"
    />
    <InGoogleDialog
      :is-open="dialog.inGoogle"
      :results="searchResults || []"
      @createFacility="createFacility"
      @importFacility="importFacility"
      @goToOwnershipUrl="goToOwnershipUrl"
      @createDuplicate="dialog.createDuplicate = true"
      @close="dialog.inGoogle = false"
    />
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import PrimaryCategoryEdit from "@/components/FacilityDetails/GmbCategories/PrimaryCategory.vue";
import AdditionalCategoriesEdit from "@/components/FacilityDetails/GmbCategories/AdditionalCategories.vue";
import FacilityCreatedDialog from "@/components/FacilityCreate/FacilityCreatedDialog.vue";
import FacilityImportedDialog from "@/components/FacilityCreate/FacilityImportedDialog.vue";
import CreateDuplicateDialog from "@/components/FacilityCreate/CreateDuplicateDialog.vue";
import InCenterDialog from "@/components/FacilityCreate/InCenterDialog.vue";
import InGoogleDialog from "@/components/FacilityCreate/InGoogle/InGoogleDialog.vue";

import cuid from "cuid";
import {
  gtmEvent__buttonClick,
  gtmEvent__locationCreate
} from "@/helpers/gtm-events";
import { CategoryType } from "@/enums/category-type";
import { GMB_SEARCH, FACILITY_CREATE_ACTION } from "@/enums/gmb-search-type";
import { GTM_LOCATION_CREATE_ACTION } from "@/enums/gtm-events";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import usersService from "@/services/users";
import facilitiesService from "@/services/facilities";
import serviceHandler from "@/helpers/handlers/service";
import ErrorHandler from "@/helpers/handlers/error";
import { pluralCount } from "@/helpers/helpers";

export default {
  name: "FacilityCreate",
  components: {
    ViewHeader,
    SectionLoader,
    PrimaryCategoryEdit,
    AdditionalCategoriesEdit,
    FacilityCreatedDialog,
    FacilityImportedDialog,
    CreateDuplicateDialog,
    InCenterDialog,
    InGoogleDialog
  },
  mixins: [permissionsInfo],
  data() {
    return {
      viewTitle: this.$t("message.facilityCreate.title"),
      loaded: false,
      loading: {
        view: false
      },
      dialog: {
        facilityCreated: false,
        facilityImported: false,
        createDuplicate: false,
        inCenter: false,
        inGoogle: false
      },

      mapFields: {
        facilityName: {
          label: this.$t("message.common.facilityName"),
          id: cuid(),
          required: true,
          value: "",
          permission: "canEditFacilityName"
        },
        street: {
          label: this.$t("message.common.street"),
          id: cuid(),
          required: true,
          value: "",
          permission: "canEditFacilityAddress"
        },
        localNumber: {
          label: this.$t("message.facilityDetails.forms.streetAndHomeNumber"),
          id: cuid(),
          required: true,
          value: "",
          permission: "canEditFacilityAddress"
        },
        postalCode: {
          label: this.$t("message.common.postalCode"),
          id: cuid(),
          required: true,
          value: "",
          permission: "canEditFacilityAddress"
        },
        city: {
          label: this.$t("message.common.city"),
          id: cuid(),
          required: true,
          value: "",
          permission: "canEditFacilityAddress"
        },
        country: {
          label: this.$t("message.common.country"),
          id: cuid(),
          required: true,
          value: "",
          permission: "canEditFacilityAddress",
          type: "autocomplete",
          items: [],
          isLoading: false
        },
        storeCode: {
          label: this.$t("message.common.storeCode"),
          id: cuid(),
          required: false,
          value: "",
          permission: "canEditFacilityStoreCode"
        }
      },
      rules: {
        required: [
          value =>
            !!value || this.$t("message.facilityCreate.baseForm.required")
        ]
      },

      primaryCategory: null,
      additionalCategories: [],

      accounts: [],
      selectedAccount: null,
      selectedProfile: null,

      searchType: null,
      searchResults: null
    };
  },

  computed: {
    mandatoryInputsFilled() {
      return Object.keys(this.mapFields).every(
        key => !!this.mapFields[key].value || !this.mapFields[key].required
      );
    },
    canProceed() {
      return (
        this.mandatoryInputsFilled &&
        !!this.primaryCategory &&
        !!this.selectedAccount &&
        !!this.selectedProfile
      );
    },
    gtmLocationsNum() {
      return this.searchResults && this.searchResults.length
        ? this.searchResults.length
        : 1;
    }
  },

  async created() {
    await this.getGoogleAccounts();
  },

  methods: {
    getFacilitiesCountLabel(value) {
      return `(${pluralCount("message.pluralForms.facility", value)})`;
    },
    commonProps(field) {
      return {
        label: field.label,
        rules: field.required ? this.rules.required : [],
        disabled: !this.getPermission(field.permission)
      };
    },
    async getCities() {
      if (this.mapFields.country.items.length) return;
      this.mapFields.country.isLoading = true;
      this.mapFields.country.items = await serviceHandler(
        facilitiesService.getCountries()
      );
      this.mapFields.country.isLoading = false;
    },
    async getGoogleAccounts() {
      this.loading.view = true;

      this.accounts = await serviceHandler(
        usersService.getGoogleOAuthAccounts()
      );

      if (this.accounts && this.accounts.length === 1) {
        this.selectedAccount = this.accounts[0];
      }

      this.loaded = true;
      this.loading.view = false;
    },

    handleAccountSelectChange(account) {
      if (this.selectedAccount && this.selectedAccount.id === account.id) {
        this.selectedProfile = null;
      }
    },

    async saveCategory({ category, type }) {
      if (type === CategoryType.PRIMARY) {
        this.primaryCategory = category;
      } else if (type === CategoryType.ADDITIONAL) {
        this.additionalCategories.push(category);
      }
    },

    async deleteCategory(deleteId) {
      this.additionalCategories = this.additionalCategories.filter(
        ({ id }) => id !== deleteId
      );
    },

    handleCancel() {
      gtmEvent__buttonClick({ label: "create_location_cancel" });

      this.$router.push({
        name: "business-addresses-list"
      });
    },

    async handleProceed() {
      this.loading.view = true;
      this.searchResults = null;
      this.searchType = null;

      const params = {
        name: this.mapFields.facilityName.value,
        address: `${this.mapFields.street.value} ${this.mapFields.localNumber.value}`,
        postal_code: this.mapFields.postalCode.value,
        city: this.mapFields.city.value,
        country: this.mapFields.country.value,
        gmb_profile_id: this.selectedProfile.id
      };

      const response = await serviceHandler(
        facilitiesService.createFacilityCheck(params)
      );

      if (response && response.type && response.results) {
        this.searchResults = this.mapResults(response.results);
        this.searchType = response.type;

        if (this.searchType === GMB_SEARCH.GOOGLE) {
          if (this.searchResults.length === 0) {
            await this.createFacility();
          } else {
            this.dialog.inGoogle = true;
          }
        } else if (this.searchType === GMB_SEARCH.CENTER) {
          gtmEvent__locationCreate({
            action: GTM_LOCATION_CREATE_ACTION.ALREADY_IN_CENTER
          });

          this.dialog.inCenter = true;
        }
      }

      this.loading.view = false;
    },

    async importFacility(gmbLocationName) {
      this.loading.view = true;
      this.dialog.inGoogle = false;

      const payload = [
        {
          name: gmbLocationName
        }
      ];

      try {
        gtmEvent__locationCreate({
          locationsNum: this.gtmLocationsNum,
          action: GTM_LOCATION_CREATE_ACTION.GMB_IMPORT
        });

        const params = {
          from_creation: true
        };

        const response = await usersService.postGoogleProfile(
          this.selectedProfile.id,
          payload,
          params
        );

        if (response && response.status === 200) {
          this.dialog.facilityImported = true;
        }
      } catch (error) {
        ErrorHandler.displayError(error);
      }

      this.loading.view = false;
    },

    goToOwnershipUrl(url) {
      gtmEvent__locationCreate({
        locationsNum: this.gtmLocationsNum,
        action: GTM_LOCATION_CREATE_ACTION.WITH_OWNER
      });

      window.open(url, "_blank");
    },

    async createFacility({ placeId = null, isDuplicate = false } = {}) {
      const payload = {
        gmb_profile: this.selectedProfile.id,
        name: this.mapFields.facilityName.value,
        street: this.mapFields.street.value,
        street_number: this.mapFields.localNumber.value,
        postal_code: this.mapFields.postalCode.value,
        city: this.mapFields.city.value,
        country: this.mapFields.country.value,
        primary_category: this.primaryCategory.id
      };

      if (this.mapFields.storeCode.value) {
        payload.store_code = this.mapFields.storeCode.value;
      }

      if (this.additionalCategories && this.additionalCategories.length) {
        payload.additional_categories = this.additionalCategories.map(
          category => category.id
        );
      }

      if (placeId) {
        payload.place_id = placeId;

        gtmEvent__locationCreate({
          locationsNum: this.gtmLocationsNum,
          action: GTM_LOCATION_CREATE_ACTION.WITHOUT_OWNER
        });
      } else {
        gtmEvent__locationCreate({
          locationsNum: this.gtmLocationsNum,
          action: isDuplicate
            ? GTM_LOCATION_CREATE_ACTION.CREATE_DUPLICATE
            : GTM_LOCATION_CREATE_ACTION.NEW_LOCATION
        });
      }
      try {
        const response = await serviceHandler(
          facilitiesService.createFacility(payload)
        );

        if (response.status === "ok") {
          this.dialog.facilityCreated = true;
        }
      } catch (e) {
        return e;
      }
    },

    resetForm() {
      [
        "street",
        "localNumber",
        "postalCode",
        "city",
        "country",
        "storeCode"
      ].forEach(key => {
        if (this.mapFields[key] && this.mapFields[key].value)
          this.mapFields[key].value = "";
      });
    },

    mapResults(results) {
      return results.map(result => {
        return { ...result, action: this.getResultAction(result) };
      });
    },

    getResultAction(result) {
      if (result) {
        if (result.exists_in_account) {
          return FACILITY_CREATE_ACTION.EXISTS_IN_ACCOUNT;
        } else {
          if (!result.ownership_url) {
            return FACILITY_CREATE_ACTION.WITHOUT_OWNER;
          } else {
            return FACILITY_CREATE_ACTION.WITH_OWNER;
          }
        }
      }
      return null;
    },

    getPermission(permissionName) {
      return this[permissionName];
    }
  }
};
</script>

<style lang="scss" scoped>
.select-item {
  display: flex;
  flex-direction: column;

  &__quantity {
    @include font-size(13px);
    color: $gray;
  }
}
.facility-create {
  &__column {
    display: flex;
    flex-direction: column;
    padding: $spacing * 4;
    padding-right: 0;

    &--secondary {
      position: relative;
      padding-left: $spacing * 5;
      padding-right: $spacing * 4;
      min-height: 400px;
      justify-content: space-between;
    }
  }

  &__heading {
    margin-bottom: $spacing * 4;
    padding-right: $spacing * 4;
  }

  &__fields-wrapper {
    display: flex;
    flex-direction: column;
  }

  &__select {
    border-bottom: 1px solid $light-3;

    &--disabled {
      opacity: 0.45;
    }
  }
}

.section {
  padding-right: $spacing * 4;
  border-right: 1px solid $light-3;

  &:not(:first-of-type) {
    margin-top: $spacing * 4;
  }
}

.section-title {
  @include sectionTitle();
}

.no-accounts {
  @include noData(650px);
  height: 100%;

  & img {
    width: 100%;
    max-height: 50%;
  }

  &__description {
    @include font-size(22px);
    width: 70%;
    margin: 0 auto;
    text-align: center;
    margin: $spacing * 5 0 $spacing * 3;
  }
}

.min-w {
  min-width: 230px !important;

  &--md {
    min-width: 130px !important;
  }
}
</style>
